import { useEffect } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { LuLink2, LuUsers } from "react-icons/lu";
import { useRecordPermissions } from "@/components/discrepancies/data-discrepancies/hooks/useRecordPermissions";

export type DiscrepanciesTab = "discrepancies" | "ssid-sync" | "mids";

interface DiscrepanciesTabsProps {
  activeTab: DiscrepanciesTab;
  onTabChange: (tab: DiscrepanciesTab) => void;
}

export function DiscrepanciesTabs({
  activeTab,
  onTabChange,
}: DiscrepanciesTabsProps) {
  const { canViewRecord } = useRecordPermissions();
  const canViewMids = canViewRecord("CHLD");

  useEffect(() => {
    if (activeTab === "mids" && !canViewMids) {
      onTabChange("discrepancies");
    }
  }, [activeTab, canViewMids, onTabChange]);

  const tabs = [
    {
      id: "discrepancies" as DiscrepanciesTab,
      label: "Data Discrepancies",
      icon: <FiAlertCircle size={16} />,
    },
    {
      id: "ssid-sync" as DiscrepanciesTab,
      label: "SSID Sync",
      icon: <LuLink2 size={16} />,
    },
    ...(canViewMids
      ? [
          {
            id: "mids" as DiscrepanciesTab,
            label: "MIDs",
            icon: <LuUsers size={16} />,
          },
        ]
      : []),
  ];

  // Roving tabindex keyboard handler per WAI-ARIA tabs pattern.
  // ArrowRight/Left moves between tabs; Home/End jump to first/last.
  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    let newIndex: number | null = null;

    if (e.key === "ArrowRight") {
      newIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      newIndex = 0;
    } else if (e.key === "End") {
      newIndex = tabs.length - 1;
    }

    if (newIndex !== null) {
      e.preventDefault();
      onTabChange(tabs[newIndex].id);
      document.getElementById(`tab-${tabs[newIndex].id}`)?.focus();
    }
  };

  return (
    <div className="tab-nav" role="tablist">
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          role="tab"
          type="button"
          id={`tab-${tab.id}`}
          aria-selected={activeTab === tab.id}
          aria-controls={`tabpanel-${tab.id}`}
          tabIndex={activeTab === tab.id ? 0 : -1}
          className={`tab-nav-btn${activeTab === tab.id ? " tab-nav-btn--active" : ""}`}
          onClick={() => onTabChange(tab.id)}
          onKeyDown={(e) => handleKeyDown(e, index)}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}
